import { Component, Input, ChangeDetectionStrategy, forwardRef, ChangeDetectorRef, ElementRef, HostListener } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface CountryOption {
  code: string;
  name: string;
  flag: string; // flag emoji or image URL
}

@Component({
  selector: 'hc-input-country-select',
  templateUrl: './input-country-select.component.html',
  styleUrls: ['./input-country-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputCountrySelectComponent),
      multi: true
    }
  ]
})
export class InputCountrySelectComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select country...';
  @Input() countries: CountryOption[] = [];
  @Input() inputId = '';
  @Input() hasError = false;
  @Input() disabled = false;

  value = '';
  isOpen = false;
  focused = false;
  searchQuery = '';

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef, private elRef: ElementRef) {}

  writeValue(value: string): void {
    this.value = value || '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  toggle(): void {
    if (this.disabled) return;
    this.isOpen = !this.isOpen;
    this.focused = this.isOpen;
    if (this.isOpen) {
      this.searchQuery = '';
    } else {
      this.onTouched();
    }
  }

  select(country: CountryOption): void {
    this.value = country.code;
    this.onChange(this.value);
    this.isOpen = false;
    this.focused = false;
    this.onTouched();
  }

  get selectedCountry(): CountryOption | undefined {
    return this.countries.find(c => c.code === this.value);
  }

  get filteredCountries(): CountryOption[] {
    if (!this.searchQuery) return this.countries;
    const query = this.searchQuery.toLowerCase();
    return this.countries.filter(c => c.name.toLowerCase().includes(query));
  }

  onSearchInput(event: Event): void {
    this.searchQuery = (event.target as HTMLInputElement).value;
    this.cdr.markForCheck();
  }

  get containerClass(): string {
    const classes = ['hc-input', 'hc-input--dropdown'];
    if (this.focused) classes.push('hc-input--focused');
    if (this.hasError) classes.push('hc-input--error');
    if (this.disabled) classes.push('hc-input--disabled');
    if (this.value) classes.push('hc-input--filled');
    return classes.join(' ');
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.focused = false;
      this.cdr.markForCheck();
    }
  }
}
